import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { getProfile, registerUser } from "../auth/auth";
import { login as loginUnified } from "../../services/authService";
import {
  decodeJwtPayload,
  getStoredAccessToken,
  setStoredAccessToken,
  clearAuthSession,
  isAccessTokenExpired,
} from "../../utils/authSession";
import {
  clearPayrollWorkflow,
  reconcilePayrollWorkflowWithUser,
} from "../payroll/payrollWorkflow";
import { isPayrollDashboardRole, normalizeRole } from "../../rbac/access";
import { queryClient } from "../../lib/queryClient";
import { clearAppQueries } from "../../services/appQueries";
import { clearDashboardCache, prefetchDashboardOverview } from "../../services/dashboardApi";
import { clearGetCoalesce } from "../../services/requestCoalesce";
import { fetchMyPageAccess } from "../../services/pageAccessService";

function resolveSessionRole(profile, payload, mode) {
  if (profile?.is_system_admin) return "admin";
  const fromProfile = profile?.role ?? profile?.employee?.role ?? profile?.user?.role;
  const fromToken = payload?.role;
  const raw = fromProfile ?? fromToken ?? (mode === "employee" ? "employee" : null);
  return raw ? normalizeRole(raw) : null;
}

export const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [principalType, setPrincipalType] = useState(null);
  const [pageAccess, setPageAccess] = useState(null);

  const loadPageAccess = useCallback(async () => {
    try {
      const access = await fetchMyPageAccess();
      setPageAccess(access);
    } catch {
      setPageAccess(null);
    }
  }, []);

  const resetLocalState = useCallback(() => {
    // Dashboard caches are not org-scoped — drop them so the next login
    // (possibly a different organisation) never sees the previous org's numbers.
    clearDashboardCache();
    clearGetCoalesce();
    clearAppQueries();
    setUser(null);
    setRole(null);
    setPrincipalType(null);
    setPageAccess(null);
  }, []);

  const bootstrapSessionFromToken = useCallback(
    async (token, modeHint = "admin") => {
      setStoredAccessToken(token);
      const payload = token ? decodeJwtPayload(token) : null;
      const principal =
        payload?.type === "employee" || modeHint === "employee" ? "employee" : "user";
      setPrincipalType(principal);

      const earlyRole = resolveSessionRole(null, payload, modeHint);
      if (earlyRole) setRole(earlyRole);

      const applyProfile = (profile) => {
        setUser(profile);
        const sessionRole = resolveSessionRole(profile, payload, modeHint);
        setRole(sessionRole);
        reconcilePayrollWorkflowWithUser(profile);
        if (isPayrollDashboardRole(sessionRole)) {
          prefetchDashboardOverview(queryClient, sessionRole);
        }
        return sessionRole;
      };

      await Promise.all([
        getProfile().then(applyProfile),
        loadPageAccess(),
      ]);
    },
    [loadPageAccess]
  );

  const loginWithToken = useCallback(
    async (token) => {
      const payload = token ? decodeJwtPayload(token) : null;
      const modeHint = payload?.type === "employee" ? "employee" : "admin";
      await bootstrapSessionFromToken(token, modeHint);
      return { access_token: token };
    },
    [bootstrapSessionFromToken]
  );

  const login = useCallback(
    async ({ mode, identifier, password, organisation_id }) => {
      clearDashboardCache();
      clearGetCoalesce();
      clearAppQueries();
      const res = await loginUnified({ mode, identifier, password, organisation_id });

      const token = getStoredAccessToken();
      const payload = token ? decodeJwtPayload(token) : null;
      const principal = payload?.type === "employee" || mode === "employee" ? "employee" : "user";
      setPrincipalType(principal);

      const earlyRole = resolveSessionRole(null, payload, mode);
      if (earlyRole) setRole(earlyRole);

      const applyProfile = (profile) => {
        setUser(profile);
        const sessionRole = resolveSessionRole(profile, payload, mode);
        setRole(sessionRole);
        reconcilePayrollWorkflowWithUser(profile);
        if (isPayrollDashboardRole(sessionRole)) {
          prefetchDashboardOverview(queryClient, sessionRole);
        }
        return sessionRole;
      };

      try {
        await Promise.all([
          getProfile().then(applyProfile),
          loadPageAccess(),
        ]);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          clearAuthSession("login_profile_401");
          resetLocalState();
          throw err;
        }
        const fallbackRole = resolveSessionRole(res, payload, mode);
        setRole(fallbackRole);
        setUser(
          res?.employee
            ? {
                legacy: true,
                employee: res.employee,
                role: res.employee?.role || fallbackRole || "employee",
              }
            : null
        );
        if (isPayrollDashboardRole(fallbackRole)) {
          prefetchDashboardOverview(queryClient, fallbackRole);
        }
        await loadPageAccess();
      }

      return res;
    },
    [resetLocalState, loadPageAccess]
  );

  const register = useCallback(async (data) => registerUser(data), []);

  const applySessionProfile = useCallback((profile) => {
    if (!profile) return null;
    setUser(profile);
    const mode = profile?.principal_type === "employee" ? "employee" : "admin";
    const sessionRole = resolveSessionRole(profile, null, mode);
    if (sessionRole) setRole(sessionRole);
    reconcilePayrollWorkflowWithUser(profile);
    return profile;
  }, []);

  const logout = useCallback(() => {
    clearAuthSession("logout");
    clearPayrollWorkflow();
    resetLocalState();
  }, [resetLocalState]);

  useEffect(() => {
    const onExpired = () => {
      resetLocalState();
    };
    window.addEventListener("auth:session-expired", onExpired);
    return () => window.removeEventListener("auth:session-expired", onExpired);
  }, [resetLocalState]);

  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => {
      loadPageAccess();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, loadPageAccess]);

  useEffect(() => {
    const token = getStoredAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }

    if (isAccessTokenExpired(token)) {
      clearAuthSession("token_expired_client");
      resetLocalState();
      setLoading(false);
      return;
    }

    const payload = decodeJwtPayload(token);
    const principal = payload?.type === "employee" ? "employee" : "user";
    setPrincipalType(principal);

    const bootstrapMode = principal === "employee" ? "employee" : "admin";
    const earlyRole = resolveSessionRole(null, payload, bootstrapMode);
    if (earlyRole) setRole(earlyRole);
    if (isPayrollDashboardRole(earlyRole)) {
      prefetchDashboardOverview(queryClient, earlyRole);
    }

    Promise.all([
      getProfile()
        .then((profile) => {
          setUser(profile);
          const sessionRole = resolveSessionRole(profile, payload, bootstrapMode);
          setRole(sessionRole);
          reconcilePayrollWorkflowWithUser(profile);
          if (isPayrollDashboardRole(sessionRole)) {
            prefetchDashboardOverview(queryClient, sessionRole);
          }
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 401) {
            clearAuthSession("bootstrap_profile_401");
          }
          resetLocalState();
        }),
      loadPageAccess(),
    ]).finally(() => setLoading(false));
  }, [resetLocalState, loadPageAccess]);

  const value = useMemo(
    () => ({
      user,
      role,
      principalType,
      pageAccess,
      loading,
      login,
      loginWithToken,
      logout,
      register,
      applySessionProfile,
      refreshPageAccess: loadPageAccess,
    }),
    [
      user,
      role,
      principalType,
      pageAccess,
      loading,
      login,
      loginWithToken,
      logout,
      register,
      applySessionProfile,
      loadPageAccess,
    ]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
